import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog'
import { Button } from './button'
import { Input } from './input'
import { Label } from './label'
import { showToast } from './toast'

export interface ProviderAccountDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

// Import auth service (will be dynamically imported to avoid build issues)
let authService: any = null
const getAuthService = async () => {
  if (!authService) {
    try {
      const module = await import('@joinomu/shared')
      authService = module.authService
    } catch (error) {
      console.warn('Auth service not available:', error)
      // Return mock service for fallback
      authService = {
        getCurrentUser: () => ({ user: null, error: null }),
        getUserProfile: () => ({ data: null, error: null }),
        updateProviderProfile: () => ({ success: true, error: null }),
        updatePassword: () => ({ success: true, error: null })
      }
    }
  }
  return authService
}

export function ProviderAccountDialog({
  open = false,
  onOpenChange
}: ProviderAccountDialogProps) {
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSaving, setIsSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [hasChanges, setHasChanges] = React.useState(false)

  // Form data state
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  })

  // Original data for change tracking
  const [originalData, setOriginalData] = React.useState({
    firstName: '',
    lastName: '',
    email: ''
  })

  // Load user profile data when dialog opens
  React.useEffect(() => {
    if (open) {
      loadUserProfile()
    }
  }, [open])

  // Track changes
  React.useEffect(() => {
    const hasFormChanges = formData.firstName !== originalData.firstName ||
                          formData.lastName !== originalData.lastName ||
                          formData.email !== originalData.email ||
                          formData.password !== ''
    setHasChanges(hasFormChanges)
  }, [formData, originalData])

  const loadUserProfile = async () => {
    try {
      setIsLoading(true)
      setError(null)
      
      const auth = await getAuthService()
      const { data, error } = await auth.getUserProfile()
      
      if (error) {
        setError('Failed to load profile data')
        console.error('Provider account dialog profile load error:', error)
      } else if (data) {
        console.log('Provider profile data loaded:', data)
        const profileData = {
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          email: data.email || ''
        }
        
        setFormData({
          ...profileData,
          password: ''
        })
        setOriginalData(profileData)
        setHasChanges(false)
      } else {
        setError('No profile data found')
      }
    } catch (err) {
      setError('Failed to load profile data')
      console.error('Provider profile load exception:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    if (!hasChanges) return

    setIsSaving(true)
    setError(null)

    try {
      const auth = await getAuthService()
      let profileUpdated = false
      let passwordUpdated = false
      let hasError = false

      // Check if there are profile changes
      const hasProfileChanges = formData.firstName !== originalData.firstName ||
                                formData.lastName !== originalData.lastName ||
                                formData.email !== originalData.email

      // Update profile if there are changes
      if (hasProfileChanges) {
        // Update profile - providers use the profiles table via updateAdminProfile
        const updateMethod = auth.updateAdminProfile || auth.updatePatientProfile
        const { success, error } = await updateMethod({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email
        })

        if (error) {
          console.error('Provider profile save error:', error)
          hasError = true
          setError('Failed to update profile information')
        } else if (success) {
          profileUpdated = true
          setOriginalData({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email
          })
        }
      }

      // Update password if provided
      if (formData.password && !hasError) {
        const { success: passwordSuccess, error: passwordError } = await auth.updatePassword('', formData.password)
        if (passwordError) {
          console.error('Provider password update error:', passwordError)
          hasError = true
          setError(passwordError.message || 'Failed to update password')
        } else if (passwordSuccess) {
          passwordUpdated = true
          setFormData(prev => ({ ...prev, password: '' }))
        }
      }

      // Show appropriate toast based on what was updated
      if (hasError) {
        showToast({
          title: 'Save Failed',
          description: 'Failed to save changes. Please try again.',
          variant: 'error'
        })
      } else if (profileUpdated && passwordUpdated) {
        showToast({
          title: 'Account Updated',
          description: 'Your profile and password have been updated successfully.',
          variant: 'success'
        })
      } else if (profileUpdated) {
        showToast({
          title: 'Account Updated',
          description: 'Your profile information has been saved successfully.',
          variant: 'success'
        })
      } else if (passwordUpdated) {
        showToast({
          title: 'Password Updated',
          description: 'Your password has been changed successfully.',
          variant: 'success'
        })
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save changes')
      console.error('Provider profile save exception:', err)
      showToast({
        title: 'Save Failed',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'error'
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      firstName: originalData.firstName,
      lastName: originalData.lastName,
      email: originalData.email,
      password: ''
    })
    setHasChanges(false)
    setError(null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Account Settings</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {error && (
            <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-sm text-muted-foreground">Loading account information...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input 
                    id="firstName" 
                    value={formData.firstName}
                    onChange={(e) => handleFormChange('firstName', e.target.value)}
                    disabled={isSaving}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input 
                    id="lastName" 
                    value={formData.lastName}
                    onChange={(e) => handleFormChange('lastName', e.target.value)}
                    disabled={isSaving}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  disabled={isSaving}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input 
                  id="password" 
                  type="password" 
                  placeholder="••••••••" 
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  disabled={isSaving}
                />
                <p className="text-sm text-muted-foreground">Leave blank to keep current password</p>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  variant="outline" 
                  disabled={isSaving}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button 
                  disabled={!hasChanges || isSaving}
                  onClick={handleSave}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}